import React, { createContext, useContext, useEffect, useState } from 'react';
import { NavLink, Link, Outlet, useNavigate } from 'react-router-dom';
import {
  Layers, Sparkles, Image as ImageIcon, Users,
  KeyRound, Coins, Shield, LogOut, Menu,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../lib/entitlements';
import { toast } from '../hooks/use-toast';
import PhotoRecipes from './PhotoRecipes';
import UgcTemplates from './UgcTemplates';
import CreateMenu from './CreateMenu';
import { Sheet, SheetContent } from './ui/sheet';

// The shell owns the catalogue modals, the Create menu and the video flag, so
// any page can open "new photo session" without re-implementing the state
// Dashboard used to hoard. Pages reach it through useShell().
const ShellContext = createContext(null);

export function useShell() {
  const ctx = useContext(ShellContext);
  if (!ctx) {
    // A page rendered outside the shell (should not happen for /app pages);
    // give it inert functions rather than a crash.
    return { openCreate: () => {}, openPhotoSessions: () => {}, openUgcVideos: () => {}, videoOn: false };
  }
  return ctx;
}

function Logo() {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
      <circle cx="5" cy="6" r="2.4" fill="#F5A623" /><circle cx="5" cy="18" r="2.4" fill="#A78BFA" />
      <circle cx="19" cy="12" r="2.4" fill="#fff" /><path d="M7 6.6 17 11.4M7 17.4 17 12.6" stroke="#3A3A42" strokeWidth="1.3" />
    </svg>
  );
}

function GroupLabel({ children }) {
  return (
    <p className="px-3 pb-1 pt-5 text-[11px] font-medium uppercase tracking-wider text-white/40">
      {children}
    </p>
  );
}

function Item({ to, end, Icon, label, right = null, onNavigate, testid }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      data-testid={testid}
      className={({ isActive }) =>
        `group flex h-9 items-center gap-2.5 rounded-lg px-3 text-sm transition-colors ${
          isActive
            ? 'bg-white/5 text-white shadow-[inset_2px_0_0_0_rgba(255,255,255,0.7)]'
            : 'text-white/55 hover:bg-white/5 hover:text-white'
        }`}
    >
      <Icon size={15} className="shrink-0" />
      <span className="truncate">{label}</span>
      {right && <span className="ml-auto shrink-0 text-xs text-white/40">{right}</span>}
    </NavLink>
  );
}

function SidebarContent({ onNavigate }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const brand = useBranding();
  const shell = useContext(ShellContext);

  return (
    <div className="flex h-full flex-col overflow-y-auto px-3 py-4">
      <Link to="/app" onClick={onNavigate} className="flex items-center gap-2 px-3 py-1">
        <Logo />
        <span className="font-serif-display text-xl">{brand.name}</span>
      </Link>

      <button
        onClick={() => { shell.openCreate(); if (onNavigate) onNavigate(); }}
        data-testid="create-open"
        className="mt-4 inline-flex h-10 items-center justify-center gap-1.5 rounded-full bg-white px-4 text-sm font-medium text-black transition-colors hover:bg-white/90"
      >
        ✦ Create
      </button>

      <GroupLabel>Library</GroupLabel>
      <nav className="flex flex-col gap-0.5">
        <Item to="/app" end Icon={Layers} label="Sessions" onNavigate={onNavigate} />
        <Item to="/app/creations" Icon={Sparkles} label="Photos & videos" onNavigate={onNavigate} testid="nav-creations" />
        <Item to="/app/assets" Icon={ImageIcon} label="Products" onNavigate={onNavigate} />
        <Item to="/app/influencers" Icon={Users} label="People" onNavigate={onNavigate} testid="nav-influencers" />
      </nav>

      <GroupLabel>Setup</GroupLabel>
      <nav className="flex flex-col gap-0.5">
        <Item to="/app/keys" Icon={KeyRound} label="API Keys" onNavigate={onNavigate} testid="nav-keys" />
        <Item
          to="/app/credits" Icon={Coins} label="Credits" onNavigate={onNavigate} testid="nav-credits"
          right={<span className="inline-flex items-center gap-1"><span className="text-node-product">{user?.role === 'admin' ? '∞' : (user?.credits_balance ?? 0)}</span></span>}
        />
        {user?.role === 'admin' && (
          <NavLink
            to="/admin"
            onClick={onNavigate}
            className="flex h-9 items-center gap-2.5 rounded-lg px-3 text-sm text-node-model transition-colors hover:bg-node-model/10"
          >
            <Shield size={15} className="shrink-0" /> Admin
          </NavLink>
        )}
      </nav>

      <div className="mt-auto flex items-center justify-between gap-2 border-t border-[#2A2A30] px-3 pb-1 pt-4">
        <span className="truncate text-sm text-white/60">{user?.name}</span>
        <button
          onClick={() => { logout(); navigate('/'); }}
          title="Log out"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-white/50 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const navigate = useNavigate();
  const brand = useBranding();
  const [drawer, setDrawer] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const [ugcOpen, setUgcOpen] = useState(false);
  // Video is off until the instance says otherwise; the flag comes from the
  // public config and failure leaves it off, which is the safe direction.
  const [videoOn, setVideoOn] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const pub = await api.publicConfig();
        setVideoOn(!!pub.video_enabled);
      } catch (e) { /* stays off */ }
    })();
  }, []);

  const newBlankCanvas = async () => {
    // No native prompt: the session is created straight away and the Studio
    // top bar already renames inline, which is where the name matters.
    try {
      const w = await api.createWorkflow({ name: 'Untitled session' });
      navigate(`/app/studio/${w.id}`);
    } catch (e) {
      toast({ title: 'Could not create the session', description: e.message });
    }
  };

  const ctx = {
    openCreate: () => setCreateOpen(true),
    // Opening a catalogue closes the Create menu: they are steps of the same
    // choice, not two dialogs to stack.
    openPhotoSessions: () => { setCreateOpen(false); setPhotoOpen(true); },
    openUgcVideos: () => { setCreateOpen(false); setUgcOpen(true); },
    newBlankCanvas,
    videoOn,
  };

  return (
    <ShellContext.Provider value={ctx}>
      <div className="min-h-screen bg-[#0E0E10] text-white">
        {/* Desktop sidebar, fixed so it stays put while the page scrolls. */}
        <aside className="fixed inset-y-0 left-0 z-40 hidden w-60 border-r border-[#2A2A30] bg-[#0C0C0E] lg:block">
          <SidebarContent />
        </aside>

        {/* Mobile top bar + drawer. */}
        <header className="sticky top-0 z-40 flex h-14 items-center justify-between border-b border-[#2A2A30] bg-[#0E0E10]/95 px-4 backdrop-blur lg:hidden">
          <Link to="/app" className="flex items-center gap-2">
            <Logo />
            <span className="font-serif-display text-lg">{brand.name}</span>
          </Link>
          <button
            onClick={() => setDrawer(true)}
            aria-label="Open menu"
            className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 hover:bg-white/5 hover:text-white"
          >
            <Menu size={18} />
          </button>
        </header>
        <Sheet open={drawer} onOpenChange={setDrawer}>
          <SheetContent side="left" className="w-72 border-r border-[#2A2A30] bg-[#0C0C0E] p-0 text-white">
            <SidebarContent onNavigate={() => setDrawer(false)} />
          </SheetContent>
        </Sheet>

        <main className="lg:pl-60">
          <Outlet />
        </main>

        {createOpen && (
          <CreateMenu
            onClose={() => setCreateOpen(false)}
            onPhoto={() => { setCreateOpen(false); setPhotoOpen(true); }}
            onUgc={() => { setCreateOpen(false); setUgcOpen(true); }}
            onBlank={() => { setCreateOpen(false); newBlankCanvas(); }}
            videoOn={videoOn}
          />
        )}
        {videoOn && ugcOpen && <UgcTemplates onClose={() => setUgcOpen(false)} />}
        {photoOpen && <PhotoRecipes onClose={() => setPhotoOpen(false)} />}
      </div>
    </ShellContext.Provider>
  );
}
